import { useState, useCallback, useEffect, useRef } from 'react';

import type { ComboboxOption } from '../combobox.types';

/**
 * Hook for managing keyboard navigation in Combobox
 *
 * Features:
 * - Arrow Up/Down navigation
 * - Home/End for first/last item
 * - Enter/Space for selection
 * - Escape to close
 * - Optional wrap-around navigation
 * - Type-ahead search
 * - Focus management
 *
 * @example
 * ```tsx
 * const {
 *   focusedIndex,
 *   handleKeyDown,
 *   setFocusedIndex,
 *   resetFocus,
 * } = useComboboxKeyboard({
 *   options: filteredOptions,
 *   isOpen,
 *   onSelect: handleSelect,
 *   onClose: handleClose,
 *   loop: true,
 * });
 * ```
 */

export interface UseComboboxKeyboardProps<TValue = string> {
  /** Array of options to navigate through */
  options: ComboboxOption<TValue>[];

  /** Whether the dropdown is open */
  isOpen: boolean;

  /** Callback when an option is selected */
  onSelect: (option: ComboboxOption<TValue>) => void;

  /** Callback when dropdown should close */
  onClose?: () => void;

  /** Whether to loop navigation (wrap around at start/end) */
  loop?: boolean;

  /** Whether to enable type-ahead search */
  typeAhead?: boolean;

  /** Type-ahead timeout in milliseconds */
  typeAheadDelay?: number;

  /** Initial focused index */
  defaultFocusedIndex?: number;
}

export interface UseComboboxKeyboardReturn {
  /** Currently focused index */
  focusedIndex: number;

  /** Set focused index programmatically */
  setFocusedIndex: (index: number) => void;

  /** Reset focus to initial state */
  resetFocus: () => void;

  /** Keyboard event handler */
  handleKeyDown: (event: React.KeyboardEvent) => void;

  /** Focus next item */
  focusNext: () => void;

  /** Focus previous item */
  focusPrevious: () => void;

  /** Focus first item */
  focusFirst: () => void;

  /** Focus last item */
  focusLast: () => void;
}

export function useComboboxKeyboard<TValue = string>({
  options,
  isOpen,
  onSelect,
  onClose,
  loop = true,
  typeAhead = true,
  typeAheadDelay = 500,
  defaultFocusedIndex = -1,
}: UseComboboxKeyboardProps<TValue>): UseComboboxKeyboardReturn {
  // Focused index state
  const [focusedIndex, setFocusedIndex] = useState(defaultFocusedIndex);

  // Type-ahead search state
  const typeAheadQuery = useRef('');
  const typeAheadTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset focus when dropdown closes or options change
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(defaultFocusedIndex);
    }
  }, [isOpen, defaultFocusedIndex]);

  // Reset focus to initial state
  const resetFocus = useCallback(() => {
    setFocusedIndex(defaultFocusedIndex);
  }, [defaultFocusedIndex]);

  // Get valid index (respecting disabled options)
  const getValidIndex = useCallback(
    (index: number): number => {
      if (options.length === 0) return -1;

      // Clamp index to valid range
      let validIndex = Math.max(0, Math.min(index, options.length - 1));

      // Skip disabled options
      while (options[validIndex]?.disabled) {
        validIndex++;
        if (validIndex >= options.length) {
          if (loop) {
            validIndex = 0;
          } else {
            return -1;
          }
        }
      }

      return validIndex;
    },
    [options, loop]
  );

  // Focus next item
  const focusNext = useCallback(() => {
    setFocusedIndex((current) => {
      if (options.length === 0) return -1;

      let nextIndex = current + 1;

      // Wrap around if loop is enabled
      if (nextIndex >= options.length) {
        return loop ? getValidIndex(0) : current;
      }

      return getValidIndex(nextIndex);
    });
  }, [options.length, loop, getValidIndex]);

  // Focus previous item
  const focusPrevious = useCallback(() => {
    setFocusedIndex((current) => {
      if (options.length === 0) return -1;

      let prevIndex = current - 1;

      // Wrap around if loop is enabled
      if (prevIndex < 0) {
        return loop ? getValidIndex(options.length - 1) : current;
      }

      return getValidIndex(prevIndex);
    });
  }, [options.length, loop, getValidIndex]);

  // Focus first item
  const focusFirst = useCallback(() => {
    setFocusedIndex(getValidIndex(0));
  }, [getValidIndex]);

  // Focus last item
  const focusLast = useCallback(() => {
    setFocusedIndex(getValidIndex(options.length - 1));
  }, [options.length, getValidIndex]);

  // Type-ahead search
  const handleTypeAhead = useCallback(
    (key: string) => {
      if (!typeAhead) return;

      // Clear existing timeout
      if (typeAheadTimeout.current) {
        clearTimeout(typeAheadTimeout.current);
      }

      // Append key to query
      typeAheadQuery.current += key.toLowerCase();

      // Find matching option
      const matchIndex = options.findIndex((option) => {
        if (option.disabled) return false;
        return option.label.toLowerCase().startsWith(typeAheadQuery.current);
      });

      if (matchIndex !== -1) {
        setFocusedIndex(matchIndex);
      }

      // Reset query after delay
      typeAheadTimeout.current = setTimeout(() => {
        typeAheadQuery.current = '';
      }, typeAheadDelay);
    },
    [options, typeAhead, typeAheadDelay]
  );

  // Keyboard event handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen) return;

      const { key } = event;

      // Navigation keys
      if (key === 'ArrowDown') {
        event.preventDefault();
        focusNext();
        return;
      }

      if (key === 'ArrowUp') {
        event.preventDefault();
        focusPrevious();
        return;
      }

      if (key === 'Home') {
        event.preventDefault();
        focusFirst();
        return;
      }

      if (key === 'End') {
        event.preventDefault();
        focusLast();
        return;
      }

      // Selection keys
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();

        if (focusedIndex >= 0 && focusedIndex < options.length) {
          const option = options[focusedIndex];
          if (option && !option.disabled) {
            onSelect(option);
          }
        }
        return;
      }

      // Close dropdown
      if (key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      // Type-ahead search (single character keys)
      if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        handleTypeAhead(key);
      }
    },
    [
      isOpen,
      focusedIndex,
      options,
      onSelect,
      onClose,
      focusNext,
      focusPrevious,
      focusFirst,
      focusLast,
      handleTypeAhead,
    ]
  );

  // Cleanup type-ahead timeout on unmount
  useEffect(() => {
    return () => {
      if (typeAheadTimeout.current) {
        clearTimeout(typeAheadTimeout.current);
      }
    };
  }, []);

  return {
    focusedIndex,
    setFocusedIndex,
    resetFocus,
    handleKeyDown,
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast,
  };
}
